import { createResourceListingPage } from '@scalingworks/refine-react-admin';
import React from 'react';
import { FulfillmentMethodType, Order } from '~/api';
import { dateFormatter, parsePrice, toCamelCaseWord } from '~/resources/helpers';
import { resourceNames } from '~/resources/resource-names';
import { Props } from './props';
import { Loading } from '~/components';
import { getOrderStatusTag, getOrderStatus } from '~/resources/helpers/order';
import { Tag } from '@scalingworks/react-admin-ui';
import { OrderStatus } from '~/config/types';
import { FullDateTimeFormat } from '~/config/constant';
import head from 'lodash/head';

export const CustomerOrder: React.FC<Props> = (props) => {
  const { loading, customerId, customerOrderIds = [] } = props;

  // ==================== HOOKS
  const listing = createResourceListingPage<Order>({
    title: '',
    showTitle: false,
    customHelmet: 'Customer',
    resourceName: resourceNames.order,
    fields: [
      'id',
      'totalQuantity',
      'createdAt',
      'subTotal',
      'totalWithTax',
      'currencyCode',
      'state',
      'orderPlacedAt',
      {
        fulfillments: [
          'method',
          {
            additionalProperties: ['courier', 'driverContact', 'driverName', 'pickUpTime'],
          },
        ],
      },
      { dineInMetadata: ['adultPax', 'kidPax', 'table'] },
      { customFields: ['shippingMethodCode'] },
    ],
    allowCreate: false,
    allowSearch: true,
    searchConfig: { placeholder: (t) => 'Search by Order ID' },
    filterConfig: { alwaysExpanded: true },
    defaultPageSize: 10,
    defaultSorter: [{ field: 'orderPlacedAt', order: 'desc' }],
    tabs: {
      options: [
        // @ts-ignore
        {
          filterValue: { field: 'id', operator: 'in', value: customerOrderIds },
          label: () => 'All',
        },
        {
          filterValue: [
            { field: 'id', operator: 'in', value: customerOrderIds },
            // @ts-ignore
            { field: 'fulfillmentMethod', operator: 'eq', value: FulfillmentMethodType.Delivery },
          ],
          label: () => 'Delivery',
        },
        {
          filterValue: [
            { field: 'id', operator: 'in', value: customerOrderIds },
            // @ts-ignore
            { field: 'fulfillmentMethod', operator: 'eq', value: FulfillmentMethodType.Pickup },
          ],
          label: () => 'Pick Up',
        },
        {
          filterValue: [
            { field: 'id', operator: 'in', value: customerOrderIds },
            // @ts-ignore
            { field: 'fulfillmentMethod', operator: 'eq', value: FulfillmentMethodType.DineIn },
          ],
          label: () => 'Dine in',
        },
      ],
    },
    filterControls: {
      createdAt: {
        type: 'daterange',
        config: {
          label: 'Order Date',
        },
      },
    },
    columns: ({ LinkToDetails, t }) => [
      {
        id: 'code',
        header: 'Order ID',
        cell: (data) => {
          const { id } = data.row.original;

          return (
            <LinkToDetails resourceId={id}>
              <div>
                <span>#{id}</span>
              </div>
            </LinkToDetails>
          );
        },
      },
      {
        id: 'deliveryMethod',
        header: t('order.columns.fulfillment', { ns: 'common', fallback: 'Fulfillment' }),
        cell: (data) => {
          const { id, customFields } = data.row.original;

          return (
            <LinkToDetails resourceId={id}>
              {toCamelCaseWord(customFields?.shippingMethodCode ?? '-')}
            </LinkToDetails>
          );
        },
      },
      {
        id: 'totalQuantity',
        header: 'Items',
        cell: (data) => {
          const { id, totalQuantity } = data.row.original;

          return (
            <LinkToDetails resourceId={id}>
              <div>
                <span>{totalQuantity}</span>
              </div>
            </LinkToDetails>
          );
        },
      },
      {
        id: 'orderPlacedAt',
        header: 'Order Date',
        cell: (data) => {
          const { id, orderPlacedAt } = data.row.original;
          const display = dateFormatter(orderPlacedAt, FullDateTimeFormat);

          return (
            <LinkToDetails resourceId={id}>
              <div>
                <span>{display}</span>
              </div>
            </LinkToDetails>
          );
        },
      },
      {
        id: 'orderState',
        header: 'Status',
        cell: (data) => {
          const { id, state } = data.row.original;
          const simplified = getOrderStatus(state) as OrderStatus;

          return (
            <LinkToDetails resourceId={id}>
              <div>
                <Tag className={getOrderStatusTag(simplified)}>
                  {t(`order.status.${simplified.toLowerCase()}`, {
                    fallback: simplified,
                    ns: 'common',
                  }).toUpperCase()}
                </Tag>
              </div>
            </LinkToDetails>
          );
        },
      },
      {
        id: 'totalWithTax',
        header: 'Amount (RM)',
        cell: (data) => {
          const { id, totalWithTax, currencyCode = 'MYR' } = data.row.original;

          return (
            <LinkToDetails resourceId={id}>
              <div>
                <span>{totalWithTax ? <span>{parsePrice(totalWithTax)}</span> : '-'}</span>
              </div>
            </LinkToDetails>
          );
        },
      },
    ],
  });
  // ==================== VIEWS
  if (loading) return <Loading />;
  if (!customerId) return null;
  return listing();
};
